import { useCallback, useMemo, useRef } from "react";
import { cn } from "@/lib/utils";
import { PlusIcon } from "lucide-react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import {
  oneDark,
  oneLight,
} from "react-syntax-highlighter/dist/esm/styles/prism";
import { useTheme } from "next-themes";
import type { ReviewComment } from "@/types/assessment";
import { CommentBadge } from "./comment-badge";
import { CommentForm } from "./comment-form";

interface CodeViewerProps {
  code: string;
  language: string;
  filename?: string;
  comments: ReviewComment[];
  activeCommentLine: number | null;
  onLineClick: (lineNumber: number | null) => void;
  onCommentSubmit: (comment: Omit<ReviewComment, "id">) => void;
  onCommentEdit: (comment: ReviewComment) => void;
  onCommentDelete: (commentId: string) => void;
  editingComment: ReviewComment | null;
  disabled?: boolean;
}

// Map our language names to Prism language identifiers
function getPrismLanguage(language: string): string {
  const languageMap: Record<string, string> = {
    go: "go",
    python: "python",
    javascript: "javascript",
    typescript: "typescript",
    java: "java",
    rust: "rust",
    cpp: "cpp",
    c: "c",
    csharp: "csharp",
    ruby: "ruby",
    swift: "swift",
    kotlin: "kotlin",
    sql: "sql",
  };
  return languageMap[language] || "javascript";
}

// Extracted style constants to prevent re-renders (fix 3.4)
const SYNTAX_HIGHLIGHTER_CUSTOM_STYLE = {
  margin: 0,
  padding: 0,
  background: "transparent",
  fontSize: "14px",
} as const;

const CODE_TAG_STYLE = {
  fontFamily: "var(--font-geist-mono), monospace",
} as const;

export function CodeViewer({
  code,
  language,
  filename,
  comments,
  activeCommentLine,
  onLineClick,
  onCommentSubmit,
  onCommentEdit,
  onCommentDelete,
  editingComment,
  disabled = false,
}: CodeViewerProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const containerRef = useRef<HTMLDivElement>(null);

  const lines = useMemo(() => code.split("\n"), [code]);

  // Group comments by start line
  const commentsByLine = useMemo(() => {
    const map = new Map<number, ReviewComment[]>();
    for (const comment of comments) {
      const lineComments = map.get(comment.start_line) || [];
      lineComments.push(comment);
      map.set(comment.start_line, lineComments);
    }
    return map;
  }, [comments]);

  const handleLineClick = useCallback(
    (lineNumber: number) => {
      if (disabled) return;
      onLineClick(lineNumber);
    },
    [disabled, onLineClick]
  );

  const scrollToLine = useCallback((lineNumber: number) => {
    const lineElement = containerRef.current?.querySelector(
      `[data-line="${lineNumber}"]`
    );
    lineElement?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  return (
    <div
      ref={containerRef}
      className="rounded-lg border overflow-hidden bg-[var(--code-bg)]"
    >
      <div className="overflow-x-auto">
        {lines.map((line, index) => {
          const lineNumber = index + 1;
          const lineComments = commentsByLine.get(lineNumber) || [];
          const isActiveComment = activeCommentLine === lineNumber;
          const isEditingThisLine = editingComment?.start_line === lineNumber;
          const hasComments = lineComments.length > 0;

          return (
            <div key={lineNumber} data-line={lineNumber}>
              {/* Code line */}
              <div
                className={cn(
                  "group flex hover:bg-[var(--code-line-hover)] transition-colors",
                  isActiveComment && "bg-[var(--code-line-active)]",
                  hasComments && "bg-[var(--code-line-hover)]/50"
                )}
              >
                {/* Gutter: Line number + Add comment button */}
                <div
                  className={cn(
                    "flex items-center gap-1 px-2 py-0.5 select-none",
                    "bg-[var(--code-gutter)] border-r border-border",
                    "min-w-[60px] justify-end"
                  )}
                >
                  {/* Add comment button - GitHub style */}
                  {!disabled && (
                    <button
                      onClick={() => handleLineClick(lineNumber)}
                      className={cn(
                        "p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity",
                        "hover:bg-primary/20 text-primary",
                        isActiveComment && "opacity-100"
                      )}
                      aria-label={`Add comment on line ${lineNumber}`}
                    >
                      <PlusIcon className="size-4" />
                    </button>
                  )}
                  {/* Line number */}
                  <span className="text-[var(--code-gutter-text)] text-sm font-mono min-w-[24px] text-right">
                    {lineNumber}
                  </span>
                </div>

                {/* Code content */}
                <div className="flex-1 px-4 py-0.5 overflow-x-auto">
                  <SyntaxHighlighter
                    language={getPrismLanguage(language)}
                    style={isDark ? oneDark : oneLight}
                    customStyle={SYNTAX_HIGHLIGHTER_CUSTOM_STYLE}
                    codeTagProps={{ style: CODE_TAG_STYLE }}
                    wrapLines={false}
                    PreTag="span"
                  >
                    {line || " "}
                  </SyntaxHighlighter>
                </div>
              </div>

              {/* Inline comments for this line */}
              {hasComments && !isEditingThisLine && (
                <div className="border-l-4 border-primary/30 bg-muted/30 pl-[60px]">
                  {lineComments.map((comment) => (
                    <InlineComment
                      key={comment.id}
                      comment={comment}
                      onEdit={() => onCommentEdit(comment)}
                      onDelete={() => onCommentDelete(comment.id)}
                      disabled={disabled}
                    />
                  ))}
                </div>
              )}

              {/* Comment form (when adding/editing) */}
              {isActiveComment && !isEditingThisLine && (
                <div className="pl-[60px] pr-4 py-2 bg-muted/30 border-l-4 border-primary">
                  <CommentForm
                    lineNumber={lineNumber}
                    filename={filename}
                    onSubmit={onCommentSubmit}
                    onCancel={() => onLineClick(null)}
                    disabled={disabled}
                    maxLine={lines.length}
                  />
                </div>
              )}

              {/* Edit form */}
              {isEditingThisLine && editingComment && (
                <div className="pl-[60px] pr-4 py-2 bg-muted/30 border-l-4 border-primary">
                  <CommentForm
                    lineNumber={lineNumber}
                    filename={filename}
                    onSubmit={onCommentSubmit}
                    onCancel={() => onCommentEdit(editingComment)}
                    initialData={editingComment}
                    disabled={disabled}
                    maxLine={lines.length}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Inline comment display (collapsed)
function InlineComment({
  comment,
  onEdit,
  onDelete,
  disabled,
}: {
  comment: ReviewComment;
  onEdit: () => void;
  onDelete: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="px-4 py-2 flex items-start gap-3 border-b border-border/50 last:border-b-0">
      {comment.category && (
        <CommentBadge category={comment.category} size="sm" showLabel={false} />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm line-clamp-2">{comment.description}</p>
        {comment.suggestion && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
            💡 {comment.suggestion}
          </p>
        )}
      </div>
      {!disabled && (
        <div className="flex items-center gap-1 text-xs">
          <button
            onClick={onEdit}
            aria-label={`Edit comment on line ${comment.start_line}`}
            className="text-muted-foreground hover:text-foreground"
          >
            Edit
          </button>
          <span className="text-muted-foreground" aria-hidden="true">·</span>
          <button
            onClick={onDelete}
            aria-label={`Delete comment on line ${comment.start_line}`}
            className="text-destructive hover:text-destructive/80"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}
